"use client";

import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { ChevronDown, Search } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const PAAS = [
  { q: "How did Dr. Jyoti Kush become a COO?", a: "Co-built CryptoMize and MaxiMize Infinium over a decade, transitioning from clinical medicine into information privacy, then intelligence, governance, and strategic operations, ultimately assuming the role of Chief Operating Officer." },
  { q: "What countries has the work covered?", a: "Worked alongside governments and global leaders in over 30 countries, with active operations across 18 on three continents. Specific nations are not named due to confidentiality agreements that govern every engagement." },
  { q: "What is the medical specialty?", a: "Gynaecology. MBBS from Delhi. The clinical background informs the operational method — the discipline of diagnosis, triage, protocol, judgment, and outcome accountability that defines the approach to organizational leadership." },
  { q: "Is Dr. Jyoti Kush available for speaking?", a: "Yes. Keynotes and presentations for organizations, conferences, universities, and leadership forums. Inquiries through official contact channels. Signature topics include operational excellence, leadership as diagnosis, the 70% Project, and building in the dark." },
  { q: "What is operational excellence?", a: "An engineered system — protocols, checklists, reviews, and outcome accountability — rather than an aspirational value. Repeatable, measurable, accountable. The thesis of the signature keynote: How Excellence Actually Runs." },
  { q: "How are women empowered?", a: "By building teams over 70% women, documenting the operating case through The 70% Project, and advocating publicly for women's empowerment. The argument is from operations, not morality — a decade of evidence that overlooked talent outperforms." },
  { q: "What is the Penta P's framework?", a: "Five interconnected strategic domains — Perception, Privacy, Politics, Policing, Policy — forming a closed-loop system. The operational architecture for Strategic Sovereignty, powering 60+ services across three continents." },
];

export function PeopleAlsoAskSection() {
  const [openIdx, setOpenIdx] = React.useState<number | null>(null);
  return (
    <Section id="people-also-ask" ariaLabel="People Also Ask" background="default">
      <SectionHeader
        icon={Search}
        overline="People Also Ask"
        overlineClassName="text-amber-gold"
        pillClassName="glass-tinted-amber-gold border-amber-gold/20"
        title={
          <>
            Common Search Queries, <span className="text-gradient-premium">Answered.</span>
          </>
        }
        subtitle="The questions search engines receive most about the operator, the organization, and the approach."
      />
      <div className="max-w-4xl mx-auto space-y-2">
        {PAAS.map((p, i) => {
          const isOpen = openIdx === i;
          return (
            <motion.div
              key={p.q}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              className="glass-card rounded-xl overflow-hidden border border-border/30"
            >
              <button
                onClick={() => setOpenIdx(isOpen ? null : i)}
                className="w-full flex items-start justify-between gap-4 p-5 text-left hover:bg-amber-gold/5 transition-colors"
              >
                <span className="text-body-md font-semibold flex-1">{p.q}</span>
                <ChevronDown className={`size-4 shrink-0 transition-transform ${isOpen ? "rotate-180 text-amber-gold" : "text-muted-foreground"}`} />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 text-body-sm text-muted-foreground leading-relaxed border-t border-border/30 pt-4">
                      {p.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
}
